import React, { useState } from "react"
import moment from "moment"
import { Box, Button, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid, Chip, Tooltip, useMediaQuery, useTheme } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import { PatientEvent } from "../../../../services/patient.service"

type Props = {
    events: PatientEvent[];
    onAddEvent: (event: PatientEvent) => void;
    onDeleteEvent: (index: number) => void;
};

export const PatientEvents: React.FC<Props> = ({ events, onAddEvent, onDeleteEvent }) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState<PatientEvent>({
        date: '',
        type: '',
        description: '',
        images: [],
    });

    const handleOpenDialog = () => {
        setFormData({
            date: '',
            type: '',
            description: '',
            images: [],
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddEvent = () => {
        if (formData.date && formData.type && formData.description) {
            onAddEvent(formData);
            handleCloseDialog();
        }
    };

    const eventTypes = ['Consulta', 'Procedimento', 'Follow-up', 'Complicação', 'Recuperação', 'Outro'];

    return (
        <Box sx={{ mt: 1 }}>
            <Button
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
                sx={{
                    mb: 2.5,
                    height: 52,
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: "13px",
                    color: "text.secondary",
                    fontSize: "13.5px",
                    fontWeight: 500,
                    backgroundColor: "transparent",
                    "&:hover": {
                        border: `2px dashed ${theme.palette.primary.main}`,
                        backgroundColor: theme.palette.brand.cream,
                        color: "primary.main",
                    },
                }}
            >
                Adicionar evento
            </Button>

            <Box sx={{ position: 'relative' }}>
                {events && events.length > 0 ? (
                    <Box sx={{ position: "relative", pl: "28px" }}>
                        <Box
                            sx={{
                                position: "absolute",
                                left: 8,
                                top: 4,
                                bottom: 4,
                                width: "2px",
                                backgroundColor: theme.palette.brand.heroBorder,
                            }}
                        />
                        {events.map((event, index) => (
                            <Box key={index} sx={{ position: "relative", mb: "18px" }}>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: -28,
                                        top: 4,
                                        width: 18,
                                        height: 18,
                                        borderRadius: "50%",
                                        backgroundColor: "background.paper",
                                        border: `3px solid ${theme.palette.primary.main}`,
                                    }}
                                />
                                <Typography sx={{ mb: "5px", fontSize: "12px", color: "text.secondary" }}>
                                    {moment(event.date).format('DD/MM/YYYY')}
                                </Typography>
                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.brand.surface2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: "12px",
                                        p: "13px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Chip
                                            label={event.type}
                                            variant="outlined"
                                            size="small"
                                            sx={{ mb: 1, fontSize: "11px", fontWeight: 600 }}
                                        />
                                        <Typography sx={{ fontSize: "13px", lineHeight: 1.5, color: theme.palette.brand.heroText }}>
                                            {event.description}
                                        </Typography>
                                        {event.images && event.images.length > 0 && (
                                            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                {event.images.map((img, idx) => (
                                                    <Box
                                                        key={idx}
                                                        component="img"
                                                        src={img}
                                                        alt={`Event ${index}-${idx}`}
                                                        sx={{ width: 60, height: 60, borderRadius: "8px", objectFit: "cover" }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                    <Tooltip title="Remover evento" placement="top">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="Remover evento"
                                            onClick={() => onDeleteEvent(index)}
                                            sx={{ ml: 1, flexShrink: 0 }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Typography color="textSecondary" align="center" sx={{ py: 2, fontSize: "13px" }}>
                        Nenhum evento registrado
                    </Typography>
                )}
            </Box>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: 22 }}>
                    Adicionar Evento
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, position: 'relative' }}>
                    <TextField
                        margin="dense"
                        name="date"
                        label="Data do Evento"
                        type="date"
                        fullWidth
                        variant="outlined"
                        value={formData.date}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required
                    />
                    <TextField
                        select
                        margin="dense"
                        name="type"
                        label="Tipo de Evento"
                        fullWidth
                        variant="outlined"
                        value={formData.type}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                            native: true,
                            MenuProps: {
                                PaperProps: {
                                    sx: { zIndex: 1400 }
                                }
                            }
                        }}
                        required
                    >
                        <option value="">Selecione um tipo</option>
                        {eventTypes.map((type) => (
                            <option key={type} value={type}>
                                {type}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        margin="dense"
                        name="description"
                        label="Descrição"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        value={formData.description}
                        onChange={handleChange}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button
                        onClick={handleAddEvent}
                        variant="contained"
                        disabled={!formData.date || !formData.type || !formData.description}
                    >
                        Adicionar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
